// ignore-string-externalization

/* eslint-disable no-unused-expressions */

import { createGlobalStyle } from 'styled-components';
import {
  screenMdMin,
  screenSmMax,
  screenSmMin,
  black,
  white,
  gray90,
} from '@spotify-internal/encore-web-v5';

import {
  gray75,
  gray60,
  gray25,
} from '../../../features/src/encore-legacy-tokens';

import { square, iconExternalLink } from './mixins';
import {
  gold,
  goldHighlight,
  navbarXsHeight,
  fontWeightBook,
  fontWeightBold,
  fontWeightBlack,
  fontSizeH3,
  fontWeightH3,
  lineHeightH3,
  fontSizeH3Sm,
  letterSpacingH3Sm,
  textColor,
} from './variables';

export const BootstrapStyles = createGlobalStyle`
  /* Links */
  a {
    transition-property: color;
    transition-duration: 0.3s;
    color: inherit;
    text-decoration: underline;

    &:hover,
    &:focus {
      text-decoration: underline;
    }

    &[class*="btn"],
    .nav &,
    .panel-title > &,
    h1 &,
    h2 &,
    h3 & {
      &,
      &:hover,
      &:focus {
        text-decoration: none;
      }
    }

    /* Standalone external links */
    &.external {
      &,
      p & {
        text-transform: uppercase;
        font-weight: ${fontWeightBold};
        color: ${gold};
        letter-spacing: 1px;
        text-decoration: inherit;

        &::after {
          content: '';
          display: inline-block;
          transition-property: opacity;
          transition-duration: 0.3s;
          background-position: bottom center;
          background-repeat: no-repeat;
          background-size: 50%;
          ${iconExternalLink(goldHighlight)};
          width: 1.25em;
          height: 1em;
          opacity: 0;
        }

        &:hover {
          color: ${goldHighlight};

          &::after {
            opacity: 1;
          }
        }
      }
    }
  }

  /* Type */

  /* Reset margins */
  h1, .h1,
  h2, .h2,
  h3, .h3 {
    margin: 0;
  }

  p {
    margin: 0 0 1.4em;
  }

  .cta-large {
    font-size: 16px;
    text-transform: uppercase;
    font-weight: ${fontWeightBold};
    letter-spacing: 0.13em;
    line-height: 16px;
  }

  .cta-medium {
    font-size: 14px;
    text-transform: uppercase;
    font-weight: ${fontWeightBold};
    letter-spacing: 0.1em;
    line-height: 16px;
  }

  .cta-small {
    font-size: 12px;
    text-transform: uppercase;
    font-weight: ${fontWeightBold};
    letter-spacing: 0.08em;
    line-height: 16px;
  }

  figcaption {
    color: ${gray60};
  }

  /* Buttons */

  .btn-group-inverse {
    .btn {
      border-radius: 0;
      letter-spacing: 0;
      font-weight: ${fontWeightBook};
      background: ${white};
      line-height: 1.5;
    }

    .btn-submenu ~ .dropdown-menu {
      border-radius: 0;
      border-bottom: 1px solid ${gray90};
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);

      li,
      li:first-child {
        border: 0;

        > a {
          color: ${gray60};
        }
      }
    }
  }

  :not(.open).btn-group-inverse .btn {
    border-bottom: 1px solid ${gray75};
    margin-bottom: -1px;
  }

  /* Navs */

  .nav-tabs {
    font-size: 12px;
    font-weight: ${fontWeightBold};
    line-height: 1;
    padding: 20px 0;
    margin-bottom: 20px;
    border: 0;
    white-space: nowrap;
    overflow: auto;

    @media (min-width: ${screenSmMin}) {
      margin-top: 15px;
      margin-bottom: 60px;
    }

    li,
    li:hover {
      float: none;
      display: inline-block;
      border-bottom: 0;
      margin-left: 0;
      margin-right: 0;
      position: relative;

      a,
      a:hover,
      a:active {
        color: red;
        color: ${gray25};
        letter-spacing: 0.14em;
        padding: 10px 20px;
        transition: color 0.3s;

        &::after {
          content: "";
          position: absolute;
          height: 3px;
          width: 32px;
          left: 50%;
          bottom: 0;
          transform: translate(-50%, 0);
          background: transparent;
          transition: background 0.3s;
        }
      }

      &:first-child {
        margin-left: -20px;
      }
    }

    li:hover,
    li:active,
    li.active {
      border-bottom: 0;
    }

    .no-touchevents & li:hover,
    .no-touchevents & li:active,
    li.active {
      a {
        color: ${black};

        &::after {
          background: ${black};
        }
      }
    }
  }

  blockquote {
    border-left: 6px solid ${gold};
    margin: 30px 15px;
    padding: 10px;

    @media (min-width: ${screenMdMin}) {
      margin: 70px;
    }

    &,
    p {
      font-size: 24px;
      font-weight: ${fontWeightBlack};
    }
  }

  .pager {
    li {
      @media (min-width: ${screenMdMin}) {
        display: inline-block;
        width: 49.5%;
        vertical-align: middle;
      }
    }

    li a {
      font-size: ${fontSizeH3};
      font-weight: ${fontWeightH3};
      color: inherit;
      line-height: ${lineHeightH3};
      max-width: 75%;
      position: relative;
      text-decoration: none;
      text-transform: none;

      @media (min-width: ${screenSmMin}) {
        font-size: ${fontSizeH3Sm};
        letter-spacing: ${letterSpacingH3Sm};
      }
    }

    .previous a {
      padding: 0 0 0 60px;
      text-align: left;
    }

    .next a {
      padding: 50px 40px;
      float: none;
      max-width: 300px;

      @media (min-width: ${screenMdMin}) {
        padding: 0 60px 0 0;
        text-align: right;
        float: right;
        max-width: none;
      }
    }

    svg {
      height: 1em;
      width: 1em;
      transition: fill 0.3s;
      position: absolute;
      top: 50%;
      fill: currentColor;

      @media (min-width: ${screenMdMin}) {
        height: 1.875em;
        width: 1.875em;
      }
    }

    .previous > a > svg {
      transform: rotate(90deg) translate(-50%,0);
      left: 0;
    }

    .next > a > svg {
      transform: rotate(-90deg) translate(50%,0);
      right: 0;
    }

    @media (min-width: ${screenMdMin}) {
      .next:only-child {
        float: right;
      }

      .previous:only-child {
        float: left;
      }
    }
  }

  /* Panels */
  .panel-group .panel {
    border-radius: 0;
    box-shadow: none;
  }

  .panel-default > .panel-heading {
    padding: 0;
    color: ${textColor};
    background: none;
    border: 0;
    position: relative;
  }

  .panel-group .panel + .panel {
    /* stylelint-disable-next-line shorthand-property-no-redundant-values */
    margin: -1px 0 0 0;
  }

  .panel-default > .panel-heading + .panel-collapse > .panel-body {
    border: 0;
    padding: 0 30px 30px;
    max-width: 900px;
  }

  .panel-title {
    @media (max-width: ${screenSmMax}) {
      font-size: 18px;
    }
  }

  .panel-title > a {
    display: block;
    padding: 20px 60px 20px 15px;
    color: inherit;

    @media (min-width: ${screenMdMin}) {
      padding: 30px 100px 30px 30px;
    }
  }

  .panel-title svg {
    position: absolute;
    right: 15px;
    top: 50%;
    margin-top: -11px;
    ${square('16px')};

    @media (min-width: ${screenMdMin}) {
      ${square('24px')};
      right: 30px;
    }
  }

  h2 a:not(.collapsed) svg {
    transform: rotate(180deg);
  }

  /* Modal */

  .modal-backdrop {
    &,
    &.am-fade {
      background: rgba(59, 59, 59, 0.8);
    }
  }

  .modal-body {
    padding: 40px;
  }

  .modal-header {
    border-bottom: 0;
  }

  .modal-inner-content {
    padding-top: 20px;
    padding-bottom: 20px;
  }

  .modal.center {

    .modal-dialog {
      margin: ${parseInt(navbarXsHeight, 10) + 20}px 20px 0;

      @media (min-width: ${screenSmMin}) {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        margin: 0;
      }
    }
  }

  .modal-content {
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  /* Forms */

  .form-control {
    height: 48px;
    padding: 6px 15px;
  }

  .form-group label,
  .radio, .radio-inline,
  .checkbox,
  .checkbox-inline {
    color: ${textColor};
  }

  .control-indicator {
    ${square('16px')};
    top: 3px;

    &::after {
      .radio &,
      .radio-inline & {
        ${square('8px')};
        top: 7px;
        left: 7px;
      }
    }
  }

  .checkbox input[type="checkbox"]:checked ~ .control-indicator,
  .checkbox-inline input[type="checkbox"]:checked ~ .control-indicator {
    ${square('16px')};
    background-size: 12px;
  }

  .radio input[type="radio"]:hover ~ .control-indicator,
  .radio-inline input[type="radio"]:hover ~ .control-indicator,
  .checkbox input[type="checkbox"]:hover ~ .control-indicator,
  .checkbox-inline input[type="checkbox"]:hover ~ .control-indicator {
    border-color: #4100F4;
  }

  .radio input[type="radio"]:checked ~ .control-indicator,
  .radio-inline input[type="radio"]:checked ~ .control-indicator,
  .checkbox input[type="checkbox"]:checked ~ .control-indicator,
  .checkbox-inline input[type="checkbox"]:checked ~ .control-indicator {
    border-color: #4100F4;
    background-color: #4100F4;
  }
`;
